import React from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useGameData } from "../Menu/GameContext";
import { gameDetails } from "../App";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "./Navbar";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const Dashboard: React.FC = () => {
    const { gameData, refreshGameData, lastRefreshed, isLoading } = useGameData();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [gameSettings, setGameSettings] = React.useState<Record<string, { visible: boolean; order: number }>>({});

    React.useEffect(() => {
        // Load game settings
        const savedSettings = localStorage.getItem('gameSettings');
        if (savedSettings) {
            setGameSettings(JSON.parse(savedSettings));
        } else {
            // Initialize with default settings
            const defaultSettings: Record<string, { visible: boolean; order: number }> = {};
            Object.keys(gameDetails).forEach((gameKey, index) => {
                defaultSettings[gameKey] = {
                    visible: true,
                    order: index
                };
            });
            setGameSettings(defaultSettings);
        }
    }, []);

    // Download all game data as JSON
    const handleDownloadAllData = () => {
        // Create a blob with the data - use compact JSON format without newlines
        const fileData = JSON.stringify(gameData);
        const blob = new Blob([fileData], { type: "application/json" });
        
        // Create a download link and trigger click
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        
        link.download = `firescore_all_games_${timestamp}.json`;
        link.href = url;
        link.click();
        
        // Clean up
        URL.revokeObjectURL(url);
    };

    const createMainChartData = (sessions: any[], gameName: string) => {
        const avgMaxLevel =
            sessions.length > 0
                ? sessions.reduce((sum: number, s: any) => sum + s.maxLevel, 0) / sessions.length
                : 0;
        const totalUsers = new Set(sessions.map((s: any) => s.userId)).size;

        return {
            bar: {
                labels: [t("max_level"), t("total_users")],
                datasets: [
                    {
                        label: `${gameName} Stats`,
                        data: [avgMaxLevel.toFixed(0), totalUsers.toFixed(0)],
                        backgroundColor: [
                            "rgba(239, 68, 68, 0.7)",  // Red (max_level)
                            "rgba(16, 185, 129, 0.7)", // Green (total_users)
                        ],
                        borderColor: [
                            "rgb(239, 68, 68)",        // Red
                            "rgb(16, 185, 129)",       // Green
                        ],
                        borderWidth: 1,
                        borderRadius: 4,
                    },
                ],
            },
        };
    };

    const createSessionChartData = (sessions: any[], gameName: string) => {
        const avgSessionTimeSeconds =
            sessions.length > 0
                ? sessions.reduce((sum: number, s: any) => sum + s.sessionTime, 0) / sessions.length
                : 0;

        // Convert seconds to minutes for the chart data
        const avgSessionTimeMinutes = avgSessionTimeSeconds / 60;

        // Calculate hours, minutes, and seconds for formatted display (e.g., in tooltips)
        const hours = Math.floor(avgSessionTimeSeconds / 3600);
        const minutes = Math.floor((avgSessionTimeSeconds % 3600) / 60);
        const seconds = Math.round(avgSessionTimeSeconds % 60);

        // Format the time for display (e.g., "8m 2s")
        const formattedTime =
            hours > 0
                ? `${hours}${t("h")} ${minutes}${t("m")} ${seconds}${t("s")}`
                : minutes > 0
                    ? `${minutes}${t("m")} ${seconds}${t("s")}`
                    : `${seconds}${t("s")}`;

        return {
            bar: {
                labels: [t("avg_session")],
                datasets: [
                    {
                        label: `${gameName} Session Time`,
                        data: [avgSessionTimeMinutes], // Use minutes for the chart data
                        backgroundColor: ["rgba(59, 130, 246, 0.7)"], // Blue (avg_session)
                        borderColor: ["rgb(59, 130, 246)"],
                        borderWidth: 1,
                        borderRadius: 4,
                    },
                ],
            },
            formattedTime, // Keep this for other uses if needed
        };
    };

    const mainChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (context: any) => {
                        const label = context.label || "";
                        const value = context.raw;
                        return `${label}: ${value}`;
                    },
                },
            },
            datalabels: {
                color: "#1f2937",
                font: { weight: 600, size: 12 },
                formatter: (value: number) => value,
                anchor: "end" as const,
                align: "top" as const,
            },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
            x: { grid: { display: false } },
        },
    };

    const sessionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (context: any) => {
                        const label = context.label || "";
                        const value = context.raw; // Value in minutes
                        const totalSeconds = value * 60; // Convert back to seconds for formatting
                        const hours = Math.floor(totalSeconds / 3600);
                        const minutes = Math.floor((totalSeconds % 3600) / 60);
                        const seconds = Math.round(totalSeconds % 60);
                        const formattedValue =
                            hours > 0
                                ? `${hours}${t("h")} ${minutes}${t("m")} ${seconds}${t("s")}`
                                : minutes > 0
                                    ? `${minutes}${t("m")} ${seconds}${t("s")}`
                                    : `${seconds}${t("s")}`;
                        return `${label}: ${formattedValue}`;
                    },
                },
            },
            datalabels: {
                color: "#1f2937",
                font: { weight: 600, size: 12 },
                formatter: (value: number) => {
                    return Math.round(value); // Display the value in minutes (e.g., 10 for 600 seconds)
                },
                anchor: "end" as const,
                align: "top" as const,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: "rgba(0, 0, 0, 0.05)" },
                ticks: {
                    callback: function (tickValue: string | number) {
                        return `${tickValue}`; // Y-axis labels in minutes
                    },
                },
            },
            x: { grid: { display: false } },
        },
    };

    const handleChartClick = (gameKey: string) => {
        navigate(`/users/${gameKey}`);
    };

    return (
        <div className="h-full bg-gray-50">
            <Navbar />
            <main className="pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">{t("dashboard")}</h1>
                        <div className="flex items-center">
                            <button
                                onClick={handleDownloadAllData}
                                disabled={Object.values(gameData).every(arr => arr.length === 0)}
                                className={`px-4 py-2 rounded-lg text-white font-medium ${
                                    Object.values(gameData).every(arr => arr.length === 0)
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-500 hover:bg-green-600"
                                }`}
                            >
                                {t("download_all_data")}
                            </button>
                        </div>
                    </div>
                    {Object.values(isLoading).some(loading => loading) ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                <p className="text-gray-600">{t("loading_data_please_wait")}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Object.entries(gameData)
                                .filter(([gameKey]) => gameSettings[gameKey]?.visible !== false)
                                .sort(([gameKeyA], [gameKeyB]) => 
                                    (gameSettings[gameKeyA]?.order || 0) - (gameSettings[gameKeyB]?.order || 0)
                                )
                                .map(([gameKey, sessions], index) => {
                                    const { name: gameName, logo } = gameDetails[gameKey];
                                    const { bar: mainChartData } = createMainChartData(sessions, gameName);
                                    const { bar: sessionChartData } = createSessionChartData(sessions, gameName);
                                    return (
                                        <motion.div
                                            key={gameKey}
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                                            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                                        >
                                            <div className="p-6">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div className="flex items-center space-x-3">
                                                        <img src={logo} alt={gameName} className="w-10 h-10 rounded-full" />
                                                        <h2 className="text-lg font-semibold text-gray-800">{gameName}</h2>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        {lastRefreshed[gameKey] && (
                                                            <span className="text-sm text-gray-500">
                                                                {t("last_updated")}: {lastRefreshed[gameKey]?.toLocaleTimeString()}
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => refreshGameData(gameKey)}
                                                            disabled={isLoading[gameKey]}
                                                            className={`p-2 rounded-lg ${
                                                                isLoading[gameKey]
                                                                ? "bg-gray-400 cursor-not-allowed" 
                                                                : "bg-blue-500 hover:bg-blue-600"
                                                            }`}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                {sessions.length > 0 ? (
                                                    <div className="space-y-4">
                                                        <div className="h-24">
                                                            <Bar data={mainChartData} options={mainChartOptions} />
                                                        </div>
                                                        <div className="h-24">
                                                            <Bar data={sessionChartData} options={sessionChartOptions} />
                                                        </div>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-2 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                                                            onClick={() => handleChartClick(gameKey)}
                                                        >
                                                            {t("view_details")}
                                                        </motion.button>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 text-center py-8">{t("loading_data", { gameName })}</p>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;